import React, { useEffect, useState } from 'react';
import { Avatar, Select, SelectItem } from '@heroui/react';
import { IdView, Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { partnerOfferService } from '../../../../api';
import {
  OnlyFansIc,
  InstagramIc,
  GoogleIc,
  TwitterIc,
  GlobalIc,
  IcReddit,
  IcAnotherSoc,
  IcTg,
  IcTikTok,
  IcYoutube,
} from '../../../../components/icons';
import { PartnerOrder } from '../../../../api/partner/partnerOfferService';
import { useIsMobile } from '../../../../hooks/useMobile';
import { useMiddleTruncate } from '../../../../hooks/useMiddleTruncate';

interface OfferSelectorProps {
  selectedOrderIds: string[];
  onSelectionChange: (orderIds: string[]) => void;
  error?: string;
  touched?: boolean;
  onClose?: () => void;
}

const getSourceIcon = (source: string, size: number = 16) => {
  const iconMap: Record<string, React.ReactElement> = {
    only_fans: <OnlyFansIc size={size} fill="#A8A8B0" />,
    reddit: <IcReddit size={size} />,
    instagram: <InstagramIc size={size} fill="#A8A8B0" />,
    tiktok: <IcTikTok size={size} />,
    google: <GoogleIc size={size} fill="#A8A8B0" />,
    telegram: <IcTg size={size} />,
    twitter: <TwitterIc size={size} fill="#A8A8B0" />,
    youtube: <IcYoutube size={size} />,
    another: <IcAnotherSoc size={size} />,
  };

  return iconMap[source] || <GlobalIc size={size} />;
};

const getSourceLabel = (source: string, t: any) => {
  const labelMap: Record<string, string> = {
    only_fans: t('proposals.onlyFans'),
    reddit: t('proposals.reddit'),
    instagram: t('proposals.instagram'),
    tiktok: t('proposals.tiktok'),
    google: t('proposals.google'),
    telegram: t('proposals.telegram'),
    twitter: t('proposals.twitter'),
    youtube: t('proposals.youtube'),
    another: t('proposals.otherOne'),
  };

  return labelMap[source] || source;
};

const getStatusLabel = (status: string, t: any) => {
  const statusMap: Record<string, string> = {
    in_progress: t('partner.orders.addPublicLinkModal.statusInProgress'),
    waiting: t('partner.orders.addPublicLinkModal.statusWaiting'),
    canceled: t('partner.orders.addPublicLinkModal.statusCanceled'),
    completed: t('partner.orders.addPublicLinkModal.statusCompleted'),
  };

  return statusMap[status] || status;
};

const getStatusColor = (status: string) => {
  const colorMap: Record<string, string> = {
    in_progress: 'text-blue-600',
    waiting: 'text-neutral-8',
    canceled: 'text-neutral-8',
    completed: 'text-neutral-8',
  };

  return colorMap[status] || 'text-gray-500';
};

const OfferSelector: React.FC<OfferSelectorProps> = ({
  selectedOrderIds,
  onSelectionChange,
  error,
  touched,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const truncateMiddle = useMiddleTruncate();
  const { t } = useTranslations();
  const [orders, setOrders] = useState<PartnerOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setIsLoading(true);
        const response = await partnerOfferService.getOrders({
          page: 1,
          page_size: 25,
        });
        setOrders(response.objects || []);
      } catch (error) {
        console.error('Error fetching orders:', error);
        setOrders([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const selectedOrders = orders.filter((order: any) =>
    selectedOrderIds.includes(String(order.model.track_link_id))
  );

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <Typography
          variant="body2"
          className={error && touched ? 'text-auth-strength-weak' : 'text-auth-ink'}
        >
          {t('partner.orders.publicLinksModal.campaign')}
          <span className={error && touched ? 'text-auth-strength-weak' : 'text-partner'}>*</span>
        </Typography>
      </div>

      <Select
        required
        aria-label="Select orders"
        selectedKeys={selectedOrderIds}
        onSelectionChange={(keys) => {
          if (typeof keys === 'string') {
            onSelectionChange([keys]);
            return;
          }

          const values = Array.from(keys) as string[];

          // Check if 'all' is selected
          if (values.includes('all') || keys === ('all' as any)) {
            const allOrderIds = orders.map((order: any) => String(order.model.track_link_id));
            onSelectionChange(allOrderIds);
            return;
          }

          onSelectionChange(values);
        }}
        onClose={onClose}
        selectionMode="multiple"
        placeholder={t('partner.orders.addPublicLinkModal.selectOrder')}
        isLoading={isLoading}
        classNames={{
          trigger: `min-h-[2.375rem] bg-white border border-[0.125rem] rounded-2 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] ${
            error && touched ? 'border-auth-strength-weak' : 'border-auth-strength-none'
          } ${selectedOrderIds.length > 0 ? 'px-1' : 'px-3'}`,
          value: 'text-sm text-zinc-500',
          popoverContent: '!rounded-2',
          listbox: 'p-2',
          label: '!mb-2',
        }}
        renderValue={(items) => {
          if (items.length === 0 || selectedOrders.length === 0)
            return t('partner.orders.addPublicLinkModal.selectOrder');

          return (
            <div className="flex items-center gap-2 w-full flex-wrap">
              {selectedOrders.map((selectedOrder) => (
                <div
                  key={selectedOrder.id}
                  className="flex bg-auth-surface items-center gap-2 rounded-1.5 px-2 py-1"
                >
                  <Avatar
                    src={selectedOrder.model.avatar}
                    showFallback
                    name={selectedOrder.model.of_user_name?.slice(0, 1)}
                    className="w-6 h-6 rounded-[0.3125rem]"
                  />
                  <Typography variant="body2">#{selectedOrder.id.slice(-4)}</Typography>
                  <button
                    className="relative shrink-0 size-3.5 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newSelection = selectedOrderIds.filter(
                        (id) => id !== String(selectedOrder.model.track_link_id)
                      );
                      onSelectionChange(newSelection);
                    }}
                  >
                    <svg className="w-3 h-3 text-neutral-8" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          );
        }}
      >
        {orders.map((order) => {
          const campaignId = order.model.link.split('/').reverse()[0];
          const truncatedCampaignId = campaignId.slice(-4);

          return (
            <SelectItem
              key={order.model.track_link_id}
              textValue={`ID: ${order.id} ${getSourceLabel(order.offer.source, t)} ${getStatusLabel(order.status, t)}`}
              classNames={{
                base: 'min-h-8 px-2 py-2 rounded-3 hover:bg-auth-surface',
              }}
            >
              <div className="flex items-center gap-3 w-full">
                {!isMobile && (
                  <Avatar
                    src={order.model.avatar}
                    showFallback
                    name={order.model.of_user_name?.slice(0, 1)}
                    className="w-6 h-6 rounded-[0.3125rem]"
                  />
                )}

                <div className="flex-1 flex gap-2 md:items-center md:flex-row flex-col">
                  <div className="flex gap-2 items-center">
                    {isMobile && (
                      <Avatar
                        src={order.model.avatar}
                        showFallback
                        name={order.model.of_user_name?.slice(0, 1)}
                        className="w-6 h-6 rounded-[0.3125rem]"
                      />
                    )}
                    <div className="flex items-center gap-2">
                      <Typography variant="body2">#{order.id.slice(-4)}</Typography>
                      <Typography variant="label3">{truncatedCampaignId}</Typography>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="flex items-center gap-2 h-6 rounded-3.5 border-[1.5px] bg-white border-auth-strength-none px-2 py-1">
                      <div className="flex items-center gap-1">
                        {getSourceIcon(order.offer.source, 14)}
                        <span className="text-auth-ink text-xs">
                          {getSourceLabel(order.offer.source, t)}
                        </span>
                      </div>
                    </div>
                    <span className={`text-xs font-medium ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status, t)}
                    </span>
                  </div>
                </div>
              </div>
            </SelectItem>
          );
        })}
      </Select>

      {error && touched && (
        <Typography variant="body2" className="text-auth-strength-weak mt-2 text-xs">
          {error}
        </Typography>
      )}
    </div>
  );
};

export default OfferSelector;
